import { useState } from 'react';
import { api, ApiError } from '../api';
import { meta } from '../module';
import { Raven } from '../components/Raven';

type CompanyChoice = { slug: string; name: string };

/** Laman utama. Aplikasi ini selalu berjalan di subdomain, jadi
 *  jalan pulang harus absolut, bukan '/'. */
const HOME = 'https://muninpartners.com';

/**
 * Split sign-in: the module speaks on the left, the form sits on the
 * right. Two reasons the form is on the right and not centred —
 * it is where the eye lands last after reading, and on a phone the
 * panes stack so the form ends up under the thumb rather than under
 * a paragraph.
 *
 * The copy is per module and comes from META, so a new subdomain
 * brings its own greeting instead of inheriting Document Control's.
 */
export function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companySlug, setCompanySlug] = useState('');
  const [choices, setChoices] = useState<CompanyChoice[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ needsCompany?: boolean; companies?: CompanyChoice[] }>(
        '/auth/login',
        { email, password, ...(companySlug ? { companySlug } : {}) },
      );
      // A user in several companies is asked which one, never guessed
      // at. Landing in the wrong tenant is the worst possible start.
      if (res?.needsCompany && res.companies) {
        setChoices(res.companies);
        setCompanySlug(res.companies[0]?.slug ?? '');
        setBusy(false);
        return;
      }
      onSignedIn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign in.');
      setBusy(false);
    }
  }

  return (
    <div className="split">
      {/* ---------- left: the module speaks ---------- */}
      <aside className="split-tell">
        <div className="split-tell-inner">
          <a className="brand" href={HOME}>
            <span className="brand-mark" aria-hidden="true">
              <Raven />
            </span>
            <span className="brand-name">
              Munin<span className="brand-tld"> Partners</span>
            </span>
          </a>

          <p className="tell-eyebrow">{meta.welcome.eyebrow}</p>
          <h1 className="tell-heading">{meta.welcome.heading}</h1>
          <p className="tell-body">{meta.welcome.body}</p>
          <p className="tell-note">{meta.welcome.note}</p>

          <p className="tell-module">
            <span>{meta.title}</span>
            {!meta.live && <span className="chip">Not built yet</span>}
          </p>
        </div>
      </aside>

      {/* ---------- right: the form ---------- */}
      <main className="split-form">
        <form className="signin-card" onSubmit={submit}>
          <a className="back-home" href={HOME}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M19 12H5M11 18l-6-6 6-6" />
            </svg>
            muninpartners.com
          </a>

          <h2>Sign in</h2>
          <p className="sub">{meta.title} · access is granted per project</p>

          {error && (
            <div className="banner err" role="alert">
              {error}
            </div>
          )}

          <label className="field">
            <span>Email</span>
            <input
              className="input"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          <label className="field">
            <span>Password</span>
            <input
              className="input"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>

          {choices && (
            <label className="field">
              <span>Company</span>
              <select
                className="select"
                value={companySlug}
                onChange={(e) => setCompanySlug(e.target.value)}
              >
                {choices.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          <button className="btn btn-primary" style={{ width: '100%' }} disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>

          <p className="hint" style={{ marginTop: 16 }}>
            No self-registration. Accounts are provisioned with the roles your work requires.
          </p>

          {/* Reviewing the interface should not require an account. */}
          <p className="hint" style={{ marginTop: 10 }}>
            <a className="link" href="/preview">
              See the {meta.title} dashboard
            </a>{' '}
            — sample data, no sign-in.
          </p>
        </form>
      </main>
    </div>
  );
}
